import { useState } from "react";
import moment from "moment";
import "./Stepper.scss";
import { useParams } from "react-router-dom";
import api from "../../utils/api";

const Stepper = ({ statusArray = [], onStatusUpdate }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStatusArray, setCurrentStatusArray] = useState(statusArray);

  // Map for translating status values to Arabic text
  const statusMap = {
    booked: "تمت الموافقة",
    onTheWay: "في الطريق",
    technicianArrived: "وصل الفني",
    inProgress: "جاري العمل",
    completed: "تم الانتهاء",
    canceled: "تم الإلغاء",
  };

  // Define the order of statuses for the stepper
  const statusOrder = [
    "booked",
    "onTheWay",
    "technicianArrived",
    "inProgress",
    "completed",
  ];

  // Format date function using moment
  const formatDate = (dateString) => {
    try {
      return moment(dateString).locale("en").format("DD/MM/YYYY - hh:mm A");
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Find the current step based on the most recent status
  const getCurrentStep = () => {
    if (currentStatusArray.length === 0) return 0;

    const isCanceled = currentStatusArray.some(
      (status) => status.value === "canceled"
    );
    if (isCanceled) return -1;

    let highestStep = 0;
    currentStatusArray.forEach((status) => {
      const stepIndex = statusOrder.indexOf(status.value);
      if (stepIndex > -1 && stepIndex + 1 > highestStep) {
        highestStep = stepIndex + 1;
      }
    });

    return highestStep;
  };

  // Get the next status value
  const getNextStatus = () => {
    const currentStep = getCurrentStep();

    // If canceled or no valid step, return null
    if (currentStep === -1 || currentStep === 0) return null;

    // If completed, no next status
    if (currentStep >= statusOrder.length) return null;

    // Return the next status in the order
    return statusOrder[currentStep];
  };

  // Get status date if available
  const getStatusDate = (statusValue) => {
    const statusItem = currentStatusArray.find((item) => item.value === statusValue);
    return statusItem ? formatDate(statusItem.date) : "";
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    const nextStatus = getNextStatus();

    if (!nextStatus) {
      console.error("No next status available");
      return;
    }

    if (!id) {
      console.error("Booking ID is required");
      setError("معرف الحجز مفقود");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Axios patch request - api instance handles base URL and headers
      const response = await api.patch(`/bookings/${id}/status`, {
        status: nextStatus,
      });

      // Axios automatically parses JSON, data is in response.data
      const data = response.data;

      // Update the status array with the new lastStatus from API response
      if (data.data?.lastStatus) {
        const newStatus = data.data.lastStatus;
        
        // Check if this status already exists in the array
        const existingStatusIndex = currentStatusArray.findIndex(
          (status) => status.value === newStatus.value
        );

        let updatedStatusArray;
        if (existingStatusIndex !== -1) {
          // Update existing status with new date
          updatedStatusArray = [...currentStatusArray];
          updatedStatusArray[existingStatusIndex] = newStatus;
        } else {
          // Add new status to the array
          updatedStatusArray = [...currentStatusArray, newStatus];
        }

        // Update local state
        setCurrentStatusArray(updatedStatusArray);

        // Call the callback function if provided with updated array
        if (onStatusUpdate) {
          onStatusUpdate(data, updatedStatusArray);
        }
      }

      // Optionally show success message
      console.log("Status updated successfully:", nextStatus);
    } catch (err) {
      console.error("Error updating status:", err);

      // Handle axios error response
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "حدث خطأ أثناء تحديث الحالة";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate current step
  const currentStep = getCurrentStep();

  // Check if order is canceled
  const isCanceled = currentStep === -1;

  // Get next status for button text
  const nextStatus = getNextStatus();
  const canProceed = nextStatus && !isCanceled;

  return (
    <div className="stepper">
      <p className="title">حالة الطلب</p>
      {isCanceled ? (
        <div className="canceled-status">
          <p className="statue">{statusMap.canceled}</p>
          <p className="date">{getStatusDate("canceled")}</p>
        </div>
      ) : (
        <div className="steps">
          {statusOrder.map((status, index) => (
            <div
              key={status}
              className={`step ${currentStep >= index + 1 ? "active" : ""}`}
            >
              <p className="circle"></p>
              <div className="content">
                <p className="statue">{statusMap[status]}</p>
                <p className="date">{getStatusDate(status)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Button */}
      {canProceed && (
        <div className="actions-btns">
          <button
            className="status-update-btn"
            onClick={handleStatusUpdate}
            disabled={isLoading}
          >
            {isLoading
              ? "جاري التحديث..."
              : `تحديث إلى: ${statusMap[nextStatus]}`}
          </button>
          {error && <p className="error-message">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default Stepper;